import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import checkAuthz from "../../../util/authz";

// Define the styles as a JavaScript object
const styles = {
  container: {
    fontFamily: "Arial, sans-serif",
    maxWidth: "1000px",
    margin: "20px auto",
    padding: "20px",
    flexGrow: 1,
  },
  header: {
    textAlign: "center",
    color: "#2E7D32",
    marginBottom: "30px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  createButton: {
    padding: "8px 16px",
    background: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "0.95em",
  },
  eventsList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "20px",
    marginTop: "30px",
  },
  eventCard: {
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "20px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    transition: "box-shadow 0.3s ease",
    textDecoration: "none",
    color: "inherit",
    display: "block",
    position: "relative",
  },
  eventCardHover: {
    boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
  },
  eventName: {
    fontSize: "1.3em",
    fontWeight: "bold",
    color: "#2E7D32",
    marginBottom: "10px",
  },
  eventDates: {
    fontSize: "0.9em",
    color: "#666",
    marginBottom: "10px",
    fontWeight: "500",
  },
  eventDescription: {
    fontSize: "0.95em",
    color: "#333",
    lineHeight: "1.5",
    marginTop: "10px",
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    fontSize: "1.1em",
    color: "#666",
  },
  noEvents: {
    textAlign: "center",
    padding: "40px",
    fontSize: "1.1em",
    color: "#666",
  },
  statusBadge: {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "0.8em",
    fontWeight: "bold",
    marginLeft: "10px",
  },
  statusCurrent: {
    backgroundColor: "#4CAF50",
    color: "white",
  },
  statusUpcoming: {
    backgroundColor: "#2196F3",
    color: "white",
  },
  statusPast: {
    backgroundColor: "#9e9e9e",
    color: "white",
  },
  menuButton: {
    position: "absolute",
    top: "12px",
    right: "12px",
    background: "none",
    border: "none",
    fontSize: "1.2em",
    cursor: "pointer",
    color: "#666",
    padding: "4px 8px",
    borderRadius: "4px",
    lineHeight: 1,
  },
  menuDropdown: {
    position: "absolute",
    top: "36px",
    right: "12px",
    background: "white",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    zIndex: 10,
  },
  menuItem: {
    display: "block",
    padding: "8px 16px",
    fontSize: "0.9em",
    color: "#333",
    textDecoration: "none",
    whiteSpace: "nowrap",
    cursor: "pointer",
    border: "none",
    background: "none",
    width: "100%",
    textAlign: "left",
  },
};

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);

  if (start && end) {
    return `${start} - ${end}`;
  } else if (start) {
    return `Starting ${start}`;
  } else if (end) {
    return `Until ${end}`;
  }
  return "Dates TBD";
};

const getEventStatus = (startDate, endDate) => {
  const now = new Date();
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;

  if (end && end < now) {
    return { status: "past", label: "Past" };
  }
  if (start && start > now) {
    return { status: "upcoming", label: "Upcoming" };
  }
  if ((!end || end >= now) && (!start || start <= now)) {
    return { status: "current", label: "Happening Now" };
  }
  return { status: "upcoming", label: "Upcoming" };
};

const EventsListingPage = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();

  const isAdmin = checkAuthz(token, ["admin"]);

  useEffect(function fetchEventsOnMount() {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const endpoint = isAdmin ? "/api/events" : "/api/events/public";
        const res = await axios.get(endpoint);
        setEvents(res.data);
        setError(null);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Failed to load events. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [isAdmin]);

  // Close menu when clicking outside
  useEffect(function setupOutsideClickToCloseMenu() {
    if (openMenu === null) return;
    const handleClick = () => setOpenMenu(null);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [openMenu]);

  const getStatusStyle = (status) => {
    if (status === "current") return styles.statusCurrent;
    if (status === "past") return styles.statusPast;
    return styles.statusUpcoming;
  };

  return (
    <>
      <Helmet>
        <title>Events | VegifyIt - Current and Upcoming Vegan Food Festivals</title>
        <meta
          name="description"
          content="Discover current and upcoming vegan food festivals and events powered by VegifyIt. Find dates, descriptions, and explore participating restaurants."
        />
        <meta
          name="keywords"
          content="vegan food festival, vegan events, vegan food events, plant-based festival, vegan festival dates"
        />
        <link rel="canonical" href="https://www.vegifyit.com/events" />
      </Helmet>
      <div style={styles.container}>
        {isAdmin ? (
          <div style={styles.headerRow}>
            <h1 style={{ ...styles.header, marginBottom: 0 }}>Events</h1>
            <button
              style={styles.createButton}
              onClick={() => navigate("/admin/events/new")}
            >
              Create Event
            </button>
          </div>
        ) : (
          <h1 style={styles.header}>Current & Upcoming Events</h1>
        )}
        <p style={{ textAlign: "center", color: "#666", marginBottom: "30px" }}>
          Explore vegan food festivals and events happening now or coming soon.
          Click on any event to discover participating restaurants and their
          vegan offerings.
        </p>

        {loading && <div style={styles.loading}>Loading events...</div>}

        {error && (
          <div style={styles.noEvents}>
            {error}
          </div>
        )}

        {!loading && !error && events.length === 0 && (
          <div style={styles.noEvents}>
            No current or upcoming events at this time. Check back soon!
          </div>
        )}

        {!loading && !error && events.length > 0 && (
          <div style={styles.eventsList}>
            {events.map((event) => {
              const eventStatus = getEventStatus(event.startDate, event.endDate);
              return (
                <Link
                  key={event._id}
                  to={`/events/${event.slug}`}
                  state={{ event }}
                  style={styles.eventCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = styles.eventCardHover.boxShadow;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = styles.eventCard.boxShadow;
                  }}
                >
                  {isAdmin && (
                    <button
                      style={styles.menuButton}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenMenu(openMenu === event._id ? null : event._id);
                      }}
                    >
                      &#8942;
                    </button>
                  )}
                  {openMenu === event._id && (
                    <div style={styles.menuDropdown}>
                      <button
                        style={styles.menuItem}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/admin/events/${event.slug}`);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "none";
                        }}
                      >
                        Edit Event
                      </button>
                    </div>
                  )}
                  <div style={styles.eventName}>
                    {event.name}
                    <span
                      style={{
                        ...styles.statusBadge,
                        ...getStatusStyle(eventStatus.status),
                      }}
                    >
                      {eventStatus.label}
                    </span>
                  </div>
                  <div style={styles.eventDates}>
                    {formatDateRange(event.startDate, event.endDate)}
                  </div>
                  {event.description && (
                    <div style={styles.eventDescription}>
                      {event.description.length > 150
                        ? `${event.description.substring(0, 150)}...`
                        : event.description}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});

export default connect(mapStateToProps)(EventsListingPage);
